import React, { ReactNode, useEffect } from 'react';

const SECTIONS: Array<{
  id: string;
  icon: ReactNode;
  heading: string;
  body: string;
  emphasis?: string;
  bullets?: Array<{ label: string; text: string }>;
}> = [
  {
    id: 'what',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
    ),
    heading: 'What are Perspectives?',
    body: 'Every major news event triggers thousands of reactions — on social media, in op-eds, across cable news, in academic analysis, and in community forums. Most of it is scattered, emotional, and algorithmically amplified to provoke rather than inform.',
    emphasis:
      'Perspectives distill what people are actually thinking into structured, readable analysis — organized by viewpoint, stripped of outrage bait.'
  },
  {
    id: 'how',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
        <path d="M8 7h6" />
        <path d="M8 11h8" />
      </svg>
    ),
    heading: 'How does the synthesis work?',
    body: 'Our AI writers scan a broad spectrum of sources — mainstream news outlets, independent journalism, policy analysis, social media discourse, and academic commentary — to map the landscape of opinion on each event.',
    emphasis:
      'They then compose original articles that represent each major viewpoint at its strongest and most coherent, the way a fair-minded advocate for that position would present it — not a caricature.'
  },
  {
    id: 'why',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    ),
    heading: 'What am I actually reading?',
    body: "You're not reading what an AI thinks — AI doesn't have political opinions. You're reading what millions of real people think, distilled into the strongest version of each position without the rage-bait and emotional manipulation that platforms optimize for.",
    bullets: [
      {
        label: 'Speed',
        text: 'Perspectives can be published hours after a story breaks, not days'
      },
      {
        label: 'Consistency',
        text: 'Every viewpoint gets the same structural treatment — premise, evidence, steelman, and a comprehension check'
      },
      {
        label: 'Fairness',
        text: 'No viewpoint gets a weaker writer. Each perspective is presented with equal rigor and good faith'
      }
    ]
  },
  {
    id: 'steelman',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M8 3H5a2 2 0 0 0-2 2v3" />
        <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
        <path d="M3 16v3a2 2 0 0 0 2 2h3" />
        <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
        <path d="m9 10 2 2 4-4" />
      </svg>
    ),
    heading: 'The steelman commitment',
    body: "Every perspective article includes a steelman section — the strongest possible case for the opposing viewpoint, written by the same persona. This isn't \"both sides\" false balance. It's intellectual honesty: if you can't articulate why thoughtful people disagree with you, you probably don't understand the issue well enough.",
    emphasis:
      "The goal isn't to tell you what to think. It's to give you a foundation solid enough to form your own view — and then share it."
  }
];

interface PerspectivesExplainerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PerspectivesExplainerModal: React.FC<PerspectivesExplainerModalProps> = ({
  isOpen,
  onClose
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
      style={{ padding: '24px' }}
    >
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)'
        }}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="perspectives-explainer-title"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-h-[85vh] overflow-hidden flex flex-col rounded-xl"
        style={{
          maxWidth: '640px',
          backgroundColor: '#FAFAF8',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          animation: 'slideUp 0.3s ease-out'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '28px 32px 20px',
            borderBottom: '1px solid #E8E6E1',
            backgroundColor: '#FAFAF8',
            flexShrink: 0
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
          >
            <div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '3px 10px',
                  backgroundColor: '#E8F5E9',
                  borderRadius: '4px',
                  marginBottom: '12px'
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#2E7D32',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase'
                  }}
                >
                  About this feature
                </span>
              </div>
              <h2
                id="perspectives-explainer-title"
                style={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontSize: '26px',
                  fontWeight: 600,
                  color: '#1A1A18',
                  margin: 0,
                  lineHeight: 1.2
                }}
              >
                How Perspectives Work
              </h2>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg border-none bg-transparent cursor-pointer text-[#8A8A85] shrink-0 -mt-0.5 hover:bg-[#EEEEE9] hover:text-[#1A1A18] transition-colors"
              aria-label="Close"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div
          className="overflow-y-auto flex-1 scrollbar-thin"
          style={{
            padding: '8px 32px 32px'
          }}
        >
          <p
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontSize: '17px',
              lineHeight: 1.65,
              color: '#4A4A45',
              margin: '20px 0 28px',
              fontStyle: 'italic'
            }}
          >
            Perspectives are AI-synthesized articles that represent what real people are thinking
            and arguing — drawn from across the media landscape, presented without the emotional
            manipulation.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            {SECTIONS.map((section) => (
              <div key={section.id}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '10px'
                  }}
                >
                  <div style={{ color: '#2E7D32', flexShrink: 0 }}>{section.icon}</div>
                  <h3
                    style={{
                      fontFamily: "'Source Serif 4', Georgia, serif",
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#1A1A18',
                      margin: 0
                    }}
                  >
                    {section.heading}
                  </h3>
                </div>

                <div style={{ paddingLeft: '34px' }}>
                  <p
                    style={{
                      fontFamily: "'Source Serif 4', Georgia, serif",
                      fontSize: '15px',
                      lineHeight: 1.7,
                      color: '#4A4A45',
                      margin: '0 0 8px'
                    }}
                  >
                    {section.body}
                  </p>

                  {section.emphasis && (
                    <p
                      style={{
                        fontFamily: "'Source Serif 4', Georgia, serif",
                        fontSize: '15px',
                        lineHeight: 1.7,
                        color: '#1A1A18',
                        margin: '10px 0 0',
                        padding: '12px 16px',
                        backgroundColor: '#F0F0EB',
                        borderLeft: '3px solid #2E7D32',
                        borderRadius: '0 6px 6px 0'
                      }}
                    >
                      {section.emphasis}
                    </p>
                  )}

                  {section.bullets && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        marginTop: '12px'
                      }}
                    >
                      {section.bullets.map((bullet) => (
                        <div
                          key={bullet.label}
                          style={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}
                        >
                          <span
                            style={{
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: '11px',
                              fontWeight: 600,
                              color: '#2E7D32',
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                              flexShrink: 0,
                              minWidth: '100px'
                            }}
                          >
                            {bullet.label}
                          </span>
                          <span
                            style={{
                              fontFamily: "'Source Serif 4', Georgia, serif",
                              fontSize: '15px',
                              lineHeight: 1.6,
                              color: '#4A4A45'
                            }}
                          >
                            {bullet.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Community section */}
          <div
            style={{
              marginTop: '32px',
              padding: '24px',
              backgroundColor: '#F5F5F0',
              borderRadius: '8px',
              border: '1px solid #E8E6E1'
            }}
          >
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#2E7D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span
                style={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#1A1A18'
                }}
              >
                The best perspectives come from you
              </span>
            </div>
            <p
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontSize: '15px',
                lineHeight: 1.7,
                color: '#4A4A45',
                margin: '0 0 12px'
              }}
            >
              AI anchors are a starting point — a way to get everyone on the same page about what
              the core arguments are before the real conversation begins. But no algorithm can
              replicate what a nurse in rural Ohio thinks about healthcare policy, or how an
              immigrant small business owner sees trade regulation. That kind of perspective only
              comes from lived experience.
            </p>
            <p
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontSize: '15px',
                lineHeight: 1.7,
                color: '#4A4A45',
                margin: '0 0 14px'
              }}
            >
              Community perspectives that earn enough recognition from readers are promoted
              alongside — and eventually above — the AI anchors. The more the community engages, the
              less the AI needs to anchor.
            </p>
            <p
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontSize: '15px',
                lineHeight: 1.7,
                color: '#4A4A45',
                margin: '0 0 14px'
              }}
            >
              Revenue from AI Perspectives funds the platform and creator incentives. Rigorous,
              multi-angle analysis that sustains itself and the community around it.
            </p>
            <p
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontSize: '15px',
                lineHeight: 1.7,
                color: '#1A1A18',
                fontWeight: 600,
                fontStyle: 'italic',
                margin: 0
              }}
            >
              The anchor perspectives map the debate. The community deepens it.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        [style*="animation: slideUp"] {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PerspectivesExplainerModal;
